import React from 'react';
import { analyzeShortTerm, formatLevel } from './marketdataChartAnalysis';

// KURZFRISTIGE (Intraday-)Gesamtanalyse aller Werte einer Watchlist (Chat vom
// 2026-08-11, Nutzervorgabe: "In der Gesamtuebersicht geht es nur um
// kurzfristige Analyse. Die mittelfristige Gesamtanalyse sehe ich in den
// Details."). Holt fuer jeden Wert 1-MINUTEN-Kerzen (Range "1m") und bewertet
// ihn ueber analyzeShortTerm() (Ampel: grün=Long, rot=Short, gelb=neutral). Am
// Ende ein Bericht: NUR die Werte mit aktuellem kurzfristigen Long- oder Short-
// Signal. Die mittelfristige Tages-Analyse steht pro Wert in der Detailansicht
// (MarketdataDetailPanel).
//
// props: x, items ([{symbol, shortlabel, price}]), watchlistName, onClose.
export default function MarketdataWatchlistAnalysis(props) {
	const x = props.x;
	const items = props.items || [];
	const [results, setResults] = React.useState([]);
	const [done, setDone] = React.useState(0);

	React.useEffect(() => {
		let cancelled = false;
		setResults([]);
		setDone(0);
		if (items.length === 0) {
			return;
		}
		// Begrenzte Parallelitaet (Chat vom 2026-08-10): jede Kerzenabfrage
		// oeffnet serverseitig eine eigene kurzlebige IB-Verbindung (siehe
		// resolveHistoricalBars). Alle auf einmal wuerden das Gateway
		// ueberlasten (Verbindungslimit ~32, "Connection reset"). Daher ein
		// kleiner Worker-Pool, der immer nur MAX_PARALLEL Abfragen offen haelt.
		const MAX_PARALLEL = 4;
		let next = 0;

		const fetchOne = (item) => {
			const finish = (candles) => {
				if (cancelled) return;
				// Live-Kurs (laufende Minute) mitgeben - wie in der Detailansicht.
				const analysis = analyzeShortTerm(candles, item.price != null ? item.price : null);
				setResults((current) => [...current, { ...item, analysis }]);
				setDone((d) => d + 1);
				startNext();
			};
			// Datenquelle wie in der Detailansicht (Chat vom 2026-08-11,
			// Nutzervorgabe "Datenqualitaet vor Tempo"): IBs LUECKENLOSE 1-Minuten-
			// Historie ("1mIB") statt des selbst mitgeschnittenen "1m"-Kanals.
			// Krypto (".CC") hat keinen IB-Kontrakt -> weiter Mitschnitt (24/7
			// nahezu lueckenlos). Node drosselt die IB-Abrufe global gegen das
			// Pacing-Limit (siehe socketServer.mjs handleIbHistoryRequest).
			const range = item.symbol.endsWith('.CC') ? '1m' : '1mIB';
			x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataChart', { symbol: item.symbol, range }, (res) => {
				finish(res.data.returncode === 1 ? (res.data.content || []) : []);
			}, () => finish([]));
		};

		const startNext = () => {
			if (cancelled || next >= items.length) return;
			const item = items[next];
			next++;
			fetchOne(item);
		};

		for (let i = 0; i < Math.min(MAX_PARALLEL, items.length); i++) {
			startNext();
		}
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const total = items.length;
	const loading = done < total;
	// Kurzfristige Ampel: grün = Long, rot = Short (gelb/neutral zaehlt nicht als
	// Chance). analyzeShortTerm liefert { ok, light, reasons }.
	const longs = results.filter((r) => r.analysis && r.analysis.ok && r.analysis.light === 'green');
	const shorts = results.filter((r) => r.analysis && r.analysis.ok && r.analysis.light === 'red');
	// Nicht auswertbar (zu wenig Intraday-Historie o.ae.) - separat gezaehlt,
	// damit klar ist, dass sie nicht etwa "keine Chance" bedeuten.
	const notEvaluable = results.filter((r) => !r.analysis || !r.analysis.ok);

	function reasonText(analysis, wantBias) {
		return (analysis.reasons || [])
			.filter((c) => c.bias === wantBias)
			.map((c) => c.label)
			.join(' · ');
	}

	// Klartext-Bericht fuer die Zwischenablage (Chat vom 2026-08-10).
	function copyToClipboard() {
		const lines = [];
		lines.push('Charttechnische Analyse · kurzfristig (Intraday)' + (props.watchlistName ? ' · ' + props.watchlistName : ''));
		lines.push(new Date().toLocaleString('de-DE'));
		lines.push('');
		const section = (title, list, bias) => {
			lines.push(title + ' (' + list.length + '):');
			if (list.length === 0) {
				lines.push('  keine');
			} else {
				list.forEach((r) => {
					lines.push('  - ' + (r.shortlabel || r.symbol) + ' (' + formatLevel(r.price) + '): ' + (reasonText(r.analysis, bias) || '—'));
				});
			}
			lines.push('');
		};
		if (longs.length === 0 && shorts.length === 0) {
			lines.push('Aktuell keine Long- oder Short-Chancen in dieser Watchlist.');
		} else {
			section('Long-Chancen', longs, 1);
			section('Short-Chancen', shorts, -1);
		}
		const text = lines.join('\n');
		const done = () => x.wah.addHint('In die Zwischenablage kopiert.');
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(text).then(done, () => x.wah.addHint('Kopieren nicht möglich.', false));
		} else {
			x.wah.addHint('Kopieren wird von diesem Browser nicht unterstützt.', false);
		}
	}

	function renderOpportunity(r, bias) {
		const a = r.analysis;
		return (
			<div className="marketdata-analysis-item" key={r.symbol}>
				<div className="marketdata-analysis-item-head">
					<span className="marketdata-analysis-item-name">{r.shortlabel || r.symbol}</span>
					<span className="marketdata-analysis-item-price">{formatLevel(r.price)}</span>
				</div>
				<div className="marketdata-analysis-item-reasons">{reasonText(a, bias) || '—'}</div>
			</div>
		);
	}

	return (
		<div className="marketdata-copy-overlay" onClick={props.onClose}>
			<div className="marketdata-analysis-dialog" onClick={(e) => e.stopPropagation()}>
				<div className="marketdata-copy-menu-head">
					<span>Charttechnik kurzfristig{props.watchlistName ? ' · ' + props.watchlistName : ''}</span>
					<span className="marketdata-analysis-head-actions">
						{!loading &&
							<i className="bi bi-clipboard marketdata-copy-menu-close" title="In Zwischenablage kopieren" onClick={copyToClipboard}></i>
						}
						<i className="bi bi-x-lg marketdata-copy-menu-close" title="Schließen" onClick={props.onClose}></i>
					</span>
				</div>

				{/* Nur der Inhalt scrollt (Chat vom 2026-08-11) - die Kopfzeile mit
				    Schliessen-Icon bleibt immer sichtbar. */}
				<div className="marketdata-analysis-body">
				{loading &&
					<p className="marketdata-copy-menu-hint">Analysiere… {done}/{total}</p>
				}

				{!loading &&
					<React.Fragment>
						{longs.length === 0 && shorts.length === 0 &&
							<p className="marketdata-copy-menu-hint">
								Aktuell bietet sich in dieser Watchlist kurzfristig kein Wert für einen Long- oder Short-Einstieg an.
							</p>
						}

						{longs.length > 0 &&
							<div className="marketdata-analysis-section">
								<div className="marketdata-analysis-section-title marketdata-analysis-long">
									<i className="bi bi-arrow-up-right-circle"></i> Long-Chancen ({longs.length})
								</div>
								{longs.map((r) => renderOpportunity(r, 1))}
							</div>
						}

						{shorts.length > 0 &&
							<div className="marketdata-analysis-section">
								<div className="marketdata-analysis-section-title marketdata-analysis-short">
									<i className="bi bi-arrow-down-right-circle"></i> Short-Chancen ({shorts.length})
								</div>
								{shorts.map((r) => renderOpportunity(r, -1))}
							</div>
						}

						{notEvaluable.length > 0 &&
							<p className="marketdata-analysis-note">
								{notEvaluable.length} Wert(e) ohne ausreichende Historie nicht bewertbar.
							</p>
						}
					</React.Fragment>
				}
				</div>
			</div>
		</div>
	);
}
